import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { LogCategory, LoggerService, LogType, LogWidget } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { KudosService } from '../../services/kudos.service';
import { PeopleService } from '../../services/people.service';
import { IWidgetComponent } from '../../shared/widget/i-widget-component';
import { Widget } from '../../shared/widget/widget';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'kudos',
    templateUrl: './kudos.component.html',
    styleUrls: ['./kudos.component.scss'],
    standalone: false
})
export class KudosComponent implements OnInit, OnDestroy, IWidgetComponent {
    public data: Widget;

    public kudosToShow : number = 3;
    public isExpanded : boolean = false; 

    public model: any = {
        recipient: {},
        message: '',
        isAnon: false
    };
    public isSearching : boolean = false;
    public searchFailed : boolean = false;
    public search = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => this.isSearching = true),
            switchMap( (query) => {
                return this.peopleService.lookupByName$(query, 0).pipe(
                    tap(() => this.searchFailed = false),
                    catchError(() => {
                        this.searchFailed = true;
                        return of([]);
                    }))
                }),
            tap(() => this.isSearching = false)
        );
    public sendMessage : string = ''; 

    @HostBinding('class')
    get size(): string {
        var widgetSize = 'col-md-' + this.data.Size.toString();

        switch (this.data.Size) {
            case 4: {
                widgetSize += ' widget-small';
                break;
            }
            case 6: {
                widgetSize += ' widget-medium';
                break;
            }
            case 12: {
                widgetSize += ' widget-large';
                break;
            }
        }

        return widgetSize;
    }

    constructor(
        private logger: LoggerService,
        public kudosService: KudosService,
        private peopleService: PeopleService,
        private toaster: ToastrService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Widget, LogWidget.Kudos, category);
    }

    ngOnInit() {
        this.log("ngOnInit", null, LogCategory.Init);
    }

    ngOnDestroy() {
        this.log("ngOnDestroy", "", LogCategory.Destroy);
    }
   
    public acceptedKudos$: Observable<any[]> = this.kudosService.myKudos$.pipe(
        tap((data: any[]) => {
            this.log('approvedKudos$', 'Filtered approved kudos', LogCategory.SubscriptionData);
        }),
        map((data: any[]) => {
            return data.filter(a => a.Status == 'Accepted');
        })
    );

    public lookupResultFormatter(value: any) {
        if (!!value)
            return value.DisplayName + (value.Description ? ` (${value.Description})` : ""); 

        return "";
    }
    public lookupInputFormatter(value: any) {
        if (!!value)
            return value.DisplayName; 
        return "";
    }

    public accept(item) {
        this.kudosService.updateKudo(item, 'Accepted'); 
        this.toaster.success('Kudo accepted');
    }
    public reject(item) {
        this.kudosService.updateKudo(item, 'Rejected');
        this.toaster.info('Kudo rejected');
    }
    public report(item) {
        this.kudosService.updateKudo(item, 'Reported');
        this.toaster.info('Kudo reported');
    }

    public sendKudo() {

        // No need to unsubscribe from finite observable 
        this.kudosService.submitKudo$(this.model.recipient.PersonId, this.model.message, this.model.isAnon)
            .subscribe(
                () => {
                    this.sendMessage = "Kudo sent! Feel free to send some more."
                    this.model.recipient = null;
                    this.model.message = "";
                    this.model.isAnon = "";
                    this.toaster.success('Kudo sent!', '', {
                        positionClass: 'toast-top-full-width'
                    });
                },
                (error) => this.sendMessage = "There was a problem submitting your kudos. " + error?.error?.Message,
            );
    }

}

